import "../../css/App.css";
import "../../css/FullEvent/FullEvent.css";
import "../../css/FullEvent/FullEventDesktop.css";
import React, {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  CustomTicketV2,
  AccountData,
  SavedFormQuestion,
  EventType,
  PromoCodeStatus,
} from "@markit/common.types";
import { isBrowser } from "react-device-detect";
import { Helmet } from "react-helmet";
import FullEventDesktop from "./FullEventDesktop";
import FullEvent from "./FullEvent";
import { MixpanelContext } from "../../context/AnalyticsService";
import {
  getVisibleCustomTickets,
  isEventExternalLink,
  isExternalLinkNoData,
  isLinkedEventType,
  isProductSpecificType,
  normalizeUrlWithHttps,
} from "@markit/common.utils";
import { eventAtCapacity } from "../../utils/eventUtils/eventUtils";
import {
  useEventSelector,
  useParametricSelector,
} from "../../hooks/useParametricSelector";
import {
  DataLoaders,
  getLoadedEvent,
  getLoadedTicket,
  getLoadedUser,
  getLoadedUsers,
} from "../../redux/slices/dataSlice";
import LoadingScreen from "../LoadingScreen";
import { useDispatch, useSelector } from "react-redux";
import { getUrlParamState } from "../../redux/slices/urlParamSlice";
import {
  LoginState,
  accountActions,
  getAccountSlice,
} from "../../redux/slices/accountSlice";
import { isRCA } from "../../utils/globalUtils";
import {
  updateLinkTrackerOpens,
  updateTotalEventLinkViews,
} from "../../utils/trackingUtils";
import { getEventFormQuestions } from "../../utils/eventUtils/formUtils";
import FullForm from "../Form/FullForm";
import { useNavigate } from "../../hooks/useNavigate";
import useAsyncEffect from "../../hooks/useAsyncEffect";
import { NavigationId } from "../../navigation/AppParamList";
import FullProduct from "../Products/FullProduct";
import { getShareDomain } from "../../utils/shareLink";
import { buildShowclixRedirectUrl } from "../../utils/navigationUtils";
import { getFormattedAddress } from "../../utils/makeEvent";

type FullEventDeciderProps = {};

const FullEventDecider = memo(function FullEventDeciderFn(
  props: FullEventDeciderProps
) {
  const dispatch = useDispatch();
  const { accountData, loggedIn } = useSelector(getAccountSlice);
  const mixpanel = useContext(MixpanelContext);
  const navigate = useNavigate();
  const [address, setAddress] = useState("");
  const [isEventAtCapacity, setIsEventAtCapacity] = useState(false);
  const [eventFormQuestions, setEventFormQuestions] = useState<
    SavedFormQuestion[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [retryAttempted, setRetryAttempted] = useState(false);
  const loadingStartRef = useRef(Date.now());
  const eventLoadedAtRef = useRef(0);
  const hostLoadedAtRef = useRef(0);
  // Per-step timings for the post-event-load async work, so a "capacity_check"
  // long-loading alert can report which awaited step actually hung.
  const phaseTimingsRef = useRef<
    Record<string, { startedAt: number; durationMs?: number }>
  >({});
  const {
    ticketId,
    promoCodeId: pCodeId,
    promoter: promoterId,
    l: currLinkId,
    linkId: oldLinkId,
    userReferrer,
  } = useSelector(getUrlParamState);

  const eventId = useEventSelector();
  const {
    isLoading: isLoadingEventData,
    data: event,
    isError: isErrorEventData,
  } = useParametricSelector(getLoadedEvent, eventId);
  const {
    isLoading: isLoadingTicketData,
    data: redeemTicket,
    isError: isErrorTicketData,
  } = useParametricSelector(getLoadedTicket, ticketId);
  const {
    isLoading: isLoadingHostData,
    data: host,
    isError: isErrorHostData,
  } = useParametricSelector(getLoadedUser, event?.createdBy ?? "");

  // Handle backsupport for old linkId param replaced for just l
  const linkId = useMemo(
    () => currLinkId || oldLinkId,
    [currLinkId, oldLinkId]
  );

  useEffect(() => {
    if (!isLoadingEventData && event && eventLoadedAtRef.current === 0) {
      eventLoadedAtRef.current = Date.now();
    }
  }, [isLoadingEventData, event]);

  useEffect(() => {
    if (!isLoadingHostData && host && hostLoadedAtRef.current === 0) {
      hostLoadedAtRef.current = Date.now();
    }
  }, [isLoadingHostData, host]);

  const isLoadingPage = useMemo(() => {
    // If we've already attempted a retry and still have an error, don't show loading
    if (retryAttempted && isErrorEventData) {
      return false;
    }

    return (
      isLoadingEventData ||
      (ticketId && isLoadingTicketData) ||
      (event && isLoadingHostData) ||
      loading
    );
  }, [
    isLoadingEventData,
    isLoadingHostData,
    isLoadingTicketData,
    loading,
    ticketId,
    event,
    retryAttempted,
    isErrorEventData,
  ]);

  const cohostsData: AccountData[] = useParametricSelector(
    getLoadedUsers,
    event?.cohosts ?? []
  )
    .map((user) => user?.data)
    .filter((user) => user !== undefined) as AccountData[];

  const [selectedCustomTickets, setSelectedCustomTickets] = useState<
    CustomTicketV2[]
  >([]);

  const isUserRCA = useMemo(
    () => (event && isRCA(event.createdBy, event.cohosts)) ?? false,
    [event]
  );

  const customTicketToRedeem = useMemo(
    () =>
      event &&
      redeemTicket &&
      event.customTickets.find(
        (ticket) => ticket.id === redeemTicket.customTicketId
      ),
    [event, redeemTicket]
  );

  const visibleTickets = useMemo(
    () =>
      event
        ? getVisibleCustomTickets(event.customTickets, customTicketToRedeem?.id)
        : [],
    [customTicketToRedeem?.id, event]
  );

  const promoCode = useMemo(() => {
    if (pCodeId && event) {
      const found = event.promoCodes.find(
        (promoCode) => promoCode.id === pCodeId
      );
      if (
        found &&
        found.redeemedBy !== "" &&
        found.redeemedBy !== "pending" &&
        found.redeemedBy !== "unlimited"
      ) {
        alert("Promo Code is already redeemed");
        return undefined;
      }
      if (found && found.status === PromoCodeStatus.INACTIVE) {
        alert("Promo Code is no longer active");
        return undefined;
      }
      return found;
    }
    return undefined;
  }, [pCodeId, event]);

  const getAddress = useCallback(async () => {
    try {
      if (event !== undefined && event.googlePlaceId !== undefined) {
        if (event.formattedAddress !== undefined) {
          setAddress(event.formattedAddress);
        } else {
          const address = await getFormattedAddress(event.googlePlaceId);
          if (address) {
            setAddress(address);
          }
        }
      }
    } catch (err: any) {
      console.error(err.message);
    }
  }, [event]);

  // Handles redirecting based on draft or live event
  // Handles redirecting if it's a role ticket
  useEffect(() => {
    if (event && event.isDraft) {
      if (accountData.inCreatorMode) {
        navigate(NavigationId.HOME_PRODUCTS);
      } else {
        navigate("/");
      }
    }
    // redirect if this is a role ticket
    if (
      ticketId &&
      redeemTicket &&
      redeemTicket.customTicketId === "" &&
      event
    ) {
      dispatch(accountActions.setCurrentRoleTicket([redeemTicket, event]));
      if (loggedIn === LoginState.LOGGED_IN) {
        window.location.replace(
          `${getShareDomain()}/e/${redeemTicket.eventId}/i/${
            accountData.uid
          }?ticketId=${ticketId}`
        );
      } else {
        navigate(NavigationId.SIGN_IN);
      }
    }
  }, [
    accountData.inCreatorMode,
    accountData.uid,
    dispatch,
    event,
    loggedIn,
    navigate,
    redeemTicket,
    ticketId,
  ]);

  // Handles the mixpanel tracking logic
  useEffect(() => {
    if (!event) {
      return;
    }
    dispatch(accountActions.setIsRCA(isUserRCA));
    if (isProductSpecificType(event.eventType)) {
      mixpanel.track("Webapp: Viewed Product Page", {
        product_id: eventId,
        type: event.eventType,
      });
    } else if (isEventExternalLink(event.eventType)) {
      mixpanel.track("Webapp: Viewed External Event Link", {
        event_id: eventId,
        external_link: event.externalLink,
        event_type: event.eventType,
      });
    } else if (ticketId) {
      mixpanel.track("Webapp: Viewed Redeeming Ticket", {
        event_id: eventId,
        tix_id: ticketId,
        event_type: "paid",
      });
    } else if (pCodeId) {
      mixpanel.track("Webapp: Viewed Redeeming Promo Code", {
        event_id: eventId,
        pCode_id: pCodeId,
        event_type: "paid",
      });
    } else if (promoterId) {
      mixpanel.track("Webapp: Viewed Promoter Link", {
        event_id: eventId,
        pCode_id: promoterId,
        event_type: "paid",
      });
    }
  }, [
    dispatch,
    event,
    eventId,
    isUserRCA,
    mixpanel,
    pCodeId,
    promoterId,
    ticketId,
  ]);

  // Handles the initial loading and refetch event attempt
  // Handles the loading of event related data (event capacity, link views, fetch event form questions)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useAsyncEffect(async () => {
    if (!event) {
      if (isErrorEventData && !retryAttempted) {
        // let new data attempt to load before it sets loading to false
        setTimeout(() => {
          // attempt a refetch of the event and ticket data. Host data will retry if event gets populated
          dispatch(DataLoaders.event(eventId));
          if (ticketId) {
            dispatch(
              DataLoaders.ticket({ eventId: eventId, itemId: ticketId })
            );
          }

          // mixpanel.track("Webapp: Refetch Attempt For Error Loading Event", {
          //   event_id: eventId,
          //   tix_id: ticketId,
          // });

          setLoading(false);
          setRetryAttempted(true);
        }, 2000);
      }
      return;
    }

    // Times an awaited step into phaseTimingsRef; a step that started but never
    // finished shows up as "pending" in the diagnostics at alert time.
    const timeStep = async <T,>(
      name: string,
      fn: () => Promise<T>
    ): Promise<T> => {
      const startedAt = Date.now();
      phaseTimingsRef.current[name] = { startedAt };
      try {
        return await fn();
      } finally {
        const entry = phaseTimingsRef.current[name];
        if (entry) entry.durationMs = Date.now() - startedAt;
      }
    };

    // track the open linkId
    if (linkId) {
      dispatch(accountActions.setLinkTrackerId(linkId));
      await timeStep("updateLinkTrackerOpens", () =>
        updateLinkTrackerOpens(eventId, event.createdBy, linkId)
      );
    } else {
      dispatch(accountActions.setLinkTrackerId(""));
    }

    // track for total views of event (w/out linkId)
    await timeStep("updateTotalEventLinkViews", () =>
      updateTotalEventLinkViews(eventId, event.createdBy)
    );

    // redirect if it's a linked event, generic link with no data, or external integration
    // Only one not to redirect on is a form (generic link with data)
    if (
      event &&
      (isExternalLinkNoData(event.eventType) ||
        isLinkedEventType(event.eventType))
    ) {
      // If it's a showclix event link, we add the linkId to the url on the redirect so it can track conversions externally
      // Currently not really utilized since comedy store sends tracking links instead of direct event links, will modify based on long-term solution
      const externalLink =
        event.eventType === EventType.EXTERNAL_SHOWCLIX && linkId
          ? buildShowclixRedirectUrl(event.externalLink, linkId)
          : event.externalLink;
      window.location.replace(normalizeUrlWithHttps(externalLink));
      return;
    }

    try {
      const [formQuestions, atCapacity] = await Promise.all([
        timeStep("getEventFormQuestions", () => getEventFormQuestions(event)),
        timeStep("eventAtCapacity", () =>
          eventAtCapacity(event, visibleTickets)
        ),
        timeStep("getAddress", () => getAddress()),
      ]);
      setEventFormQuestions(formQuestions);
      setIsEventAtCapacity(atCapacity);
      setLoading(false);
    } catch (err) {
      // Essential data (form questions / capacity) failed to load after the
      // retry. Leave the page in the loading state so the user can reload,
      // rather than rendering without it or setting loading to false.
      console.error("Essential event data failed to load:", err);
    }
  }, [
    customTicketToRedeem,
    dispatch,
    event,
    eventId,
    getAddress,
    isErrorEventData,
    linkId,
    retryAttempted,
    ticketId,
    visibleTickets,
  ]);

  // Formats the per-step timings for the long-loading alert / telemetry. A step
  // still in flight is reported as "pending(Xs)" — that's the one that hung.
  const getLoadDiagnostics = useCallback(() => {
    const now = Date.now();
    const timings = Object.entries(phaseTimingsRef.current);
    if (timings.length === 0) return "none";
    return timings
      .map(([name, t]) =>
        t.durationMs !== undefined
          ? `${name}=${(t.durationMs / 1000).toFixed(1)}s`
          : `${name}=pending(${((now - t.startedAt) / 1000).toFixed(1)}s)`
      )
      .join(", ");
  }, []);

  if (isLoadingPage) {
    // Distinguish a failed load (errored) from a slow one still in progress.
    // An errored event leaves the async effect returning early on `!event`, so
    // it would otherwise show as "capacity_check" with no phase_timings.
    const loadingPhase = isErrorEventData
      ? "event_error"
      : isLoadingEventData
        ? "event_fetch"
        : isErrorHostData
          ? "host_error"
          : isLoadingHostData
            ? "host_fetch"
            : "capacity_check";
    return (
      <LoadingScreen
        isLoadingPage={isLoadingPage}
        loadingEventId={eventId}
        loadingPhase={loadingPhase}
        loadingStartTime={loadingStartRef.current}
        getDiagnostics={getLoadDiagnostics}
      />
    );
  }

  // Show error screen if:
  // 1. There's an explicit error in event data
  // 2. There's an explicit error in ticket data
  // 3. The event exists but there's an error loading host data
  // 4. We've attempted a retry but still don't have an event
  if (
    isErrorEventData ||
    isErrorTicketData ||
    (event && isErrorHostData) ||
    (retryAttempted && !event) ||
    (!isLoadingEventData && !event)
  ) {
    return <LoadingScreen error loadingEventId={eventId} />;
  }

  // If we get here and don't have an event or host, something's wrong
  if (!event || !host) {
    return <LoadingScreen error loadingEventId={eventId} />;
  }

  // Matches the server-side title set in functions/index.js for social
  // crawlers/link previews, so the client-rendered tab title agrees with it.
  const pageTitle = event.title ? event.title : "Markit Event";
  const renderHelmet = (
    <Helmet>
      <title>{pageTitle}</title>
    </Helmet>
  );

  // Products
  if (isProductSpecificType(event.eventType)) {
    return (
      <>
        {renderHelmet}
        <FullProduct
          product={event}
          host={host}
          isAtCapacity={isEventAtCapacity}
          formQuestions={eventFormQuestions}
        />
      </>
    );
  }

  // Forms
  if (event.eventType === EventType.GENERIC_LINK) {
    return (
      <>
        {renderHelmet}
        <FullForm
          event={event}
          host={host}
          eventFormQuestions={eventFormQuestions}
        />
      </>
    );
  }

  if (isBrowser) {
    return (
      <>
        {renderHelmet}
        <FullEventDesktop
          event={event}
          cohostsList={cohostsData}
          host={host}
          posts={[]}
          address={address}
          tixId={ticketId}
          ticketData={redeemTicket}
          ticketToRedeem={customTicketToRedeem}
          promoCode={promoCode}
          promoterId={promoterId ?? ""}
          isEventAtCapacity={isEventAtCapacity}
          userReferrer={userReferrer ?? ""}
          selectedCustomTickets={selectedCustomTickets}
          setSelectedCustomTickets={setSelectedCustomTickets}
          eventFormQuestions={eventFormQuestions}
        />
      </>
    );
  } else {
    return (
      <>
        {renderHelmet}
        <FullEvent
          event={event}
          cohostsList={cohostsData}
          host={host}
          posts={[]}
          address={address}
          tixId={ticketId}
          ticketData={redeemTicket}
          ticketToRedeem={customTicketToRedeem}
          promoCode={promoCode}
          promoterId={promoterId ?? ""}
          isEventAtCapacity={isEventAtCapacity}
          userReferrer={userReferrer ?? ""}
          selectedCustomTickets={selectedCustomTickets}
          setSelectedCustomTickets={setSelectedCustomTickets}
          eventFormQuestions={eventFormQuestions}
        />
      </>
    );
  }
});

export default FullEventDecider;
